import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  FormField,
  Select,
  Button,
  Alert,
  StatusIndicator,
} from '@amzn/awsui-components-react';

import { connect, listTables, listColumns } from '../onboardingApi';
import { OnboardingApiError } from '../onboardingApi';
import { SECRET_ARN_PATTERN } from '../onboardingValidators';
import { SECRET_ENV_OPTIONS, SecretEnv } from '../onboardingReducer';
import {
  RedshiftConnectionRequest,
  ConnectResponse,
  TablesRequest,
  TablesResponse,
  ColumnsRequest,
  ColumnsResponse,
  TableInfo,
} from '../types';

export interface RedshiftConnectPanelProps {
  /** The three per-environment secret ARNs entered on the merged step. */
  secretArns: { test: string; beta: string; live: string };
  onColumnsLoaded: (result: ColumnsResponse) => void;
}

function formatRowCount(count: number | null): string {
  if (count === null) return '';
  if (count >= 1_000_000) {
    return `(${(count / 1_000_000).toFixed(1)}M rows)`;
  }
  if (count >= 1_000) {
    return `(${(count / 1_000).toFixed(1)}K rows)`;
  }
  return `(${count} rows)`;
}

const RedshiftConnectPanel: React.FC<RedshiftConnectPanelProps> = ({ secretArns, onColumnsLoaded }) => {
  // Introspection connects with one of the three secret ARNs entered above on
  // the merged step. The user picks which environment to connect with; host,
  // port, and database are resolved from that secret.
  const [connectEnv, setConnectEnv] = useState<SecretEnv>('test');
  const secretArn = secretArns[connectEnv];

  // Loading states
  const [connecting, setConnecting] = useState(false);
  const [fetchingTables, setFetchingTables] = useState(false);
  const [fetchingColumns, setFetchingColumns] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [schemas, setSchemas] = useState<string[]>([]);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedSchema, setSelectedSchema] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);

  // Connection success flag
  const [isConnected, setIsConnected] = useState(false);

  // Generation counter to discard stale schema-change responses
  const schemaFetchGeneration = useRef(0);

  // Reset all connection-derived state when the user switches environment.
  // Without this, the schemas/tables from the previous env stay displayed while
  // subsequent /tables and /columns calls use the new env's secretArn — fetching
  // with the wrong credentials or showing a confusing schema/credential mismatch.
  // The user must re-Connect with the newly selected environment.
  useEffect(() => {
    setIsConnected(false);
    setSchemas([]);
    setTables([]);
    setSelectedSchema(null);
    setSelectedTable(null);
    setError(null);
  }, [connectEnv]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    setSchemas([]);
    setSelectedSchema(null);
    setTables([]);
    setSelectedTable(null);
    setIsConnected(false);

    try {
      const request: RedshiftConnectionRequest = {
        secret_arn: secretArn,
      };
      const response: ConnectResponse = await connect(request);
      setSchemas(response.schemas);
      setIsConnected(true);
    } catch (err) {
      const message =
        err instanceof OnboardingApiError
          ? err.message
          : 'Connection failed. Please check your secret ARN.';
      setError(message);
    } finally {
      setConnecting(false);
    }
  }, [secretArn]);

  const handleSchemaChange = useCallback(
    async (schema: string) => {
      const generation = ++schemaFetchGeneration.current;
      setSelectedSchema(schema);
      setTables([]);
      setSelectedTable(null);
      setFetchingTables(true);
      setError(null);

      try {
        const request: TablesRequest = {
          secret_arn: secretArn,
          schema_name: schema,
        };
        const response: TablesResponse = await listTables(request);
        if (generation !== schemaFetchGeneration.current) return; // stale response
        setTables(response.tables);
      } catch (err) {
        if (generation !== schemaFetchGeneration.current) return; // stale error
        const message =
          err instanceof OnboardingApiError
            ? err.message
            : 'Failed to fetch tables for the selected schema.';
        setError(message);
      } finally {
        if (generation === schemaFetchGeneration.current) {
          setFetchingTables(false);
        }
      }
    },
    [secretArn],
  );

  const handleFetchColumns = useCallback(async () => {
    if (!selectedSchema || !selectedTable) return;

    setFetchingColumns(true);
    setError(null);

    try {
      const request: ColumnsRequest = {
        secret_arn: secretArn,
        schema_name: selectedSchema,
        table_name: selectedTable,
      };
      const response: ColumnsResponse = await listColumns(request);
      onColumnsLoaded(response);
    } catch (err) {
      const message =
        err instanceof OnboardingApiError
          ? err.message
          : 'Failed to fetch columns for the selected table.';
      setError(message);
    } finally {
      setFetchingColumns(false);
    }
  }, [secretArn, selectedSchema, selectedTable, onColumnsLoaded]);

  const isFormValid = SECRET_ARN_PATTERN.test(secretArn.trim());

  const tableOptions = tables.map((t) => ({
    value: t.tableName,
    label: `${t.tableName} ${formatRowCount(t.estimatedRowCount)}`.trim(),
  }));

  return (
    <Container header={<Header variant="h2">Connect to Redshift</Header>}>
      <SpaceBetween size="l">
        {/* The connection uses one of the secret ARNs entered above; host, port,
            and database are resolved from that secret. */}
        <FormField
          label="Connect using"
          description="Which environment's secret ARN to introspect with. Enter the ARNs in the Database configuration section above."
        >
          <Select
            selectedOption={
              SECRET_ENV_OPTIONS.find((o) => o.value === connectEnv) ?? null
            }
            onChange={({ detail }) =>
              setConnectEnv((detail.selectedOption.value as SecretEnv) ?? 'test')
            }
            options={SECRET_ENV_OPTIONS}
            ariaLabel="Connect using environment"
          />
        </FormField>

        <SpaceBetween size="s" direction="horizontal">
          <Button
            variant="primary"
            loading={connecting}
            disabled={!isFormValid}
            onClick={handleConnect}
          >
            Connect
          </Button>
          {isConnected && <StatusIndicator type="success">Connected</StatusIndicator>}
        </SpaceBetween>

        {!isFormValid && (
          <StatusIndicator type="info">
            Enter a valid {connectEnv} secret ARN in the Database configuration section above to connect.
          </StatusIndicator>
        )}

        {/* Schema Dropdown — shown after successful connection */}
        {isConnected && schemas.length > 0 && (
          <FormField label="Schema">
            <Select
              selectedOption={
                selectedSchema ? { value: selectedSchema, label: selectedSchema } : null
              }
              onChange={({ detail }) => {
                const schema = detail.selectedOption.value;
                if (schema) {
                  handleSchemaChange(schema);
                }
              }}
              options={schemas.map((s) => ({ value: s, label: s }))}
              placeholder="Select a schema"
              loadingText="Fetching tables..."
              statusType={fetchingTables ? 'loading' : 'finished'}
            />
          </FormField>
        )}

        {/* Loading state — shown while tables for the selected schema are loading.
            The schema Select's own spinner only shows inside the open popover, so
            this gives visible feedback after the dropdown closes. */}
        {selectedSchema && fetchingTables && (
          <StatusIndicator type="loading">Loading tables...</StatusIndicator>
        )}

        {/* Empty state — shown when schema is selected but has no tables */}
        {selectedSchema && !fetchingTables && tables.length === 0 && (
          <StatusIndicator type="info">No tables found in this schema.</StatusIndicator>
        )}

        {/* Table Dropdown — shown after schema is selected and tables are loaded */}
        {selectedSchema && tables.length > 0 && (
          <FormField label="Table">
            <Select
              selectedOption={
                selectedTable
                  ? tableOptions.find((o) => o.value === selectedTable) ?? null
                  : null
              }
              onChange={({ detail }) => {
                setSelectedTable(detail.selectedOption.value ?? null);
              }}
              options={tableOptions}
              placeholder="Select a table"
            />
          </FormField>
        )}

        {/* Fetch Columns Button — shown after table is selected */}
        {selectedTable && (
          <Button
            variant="primary"
            loading={fetchingColumns}
            onClick={handleFetchColumns}
          >
            Fetch Columns
          </Button>
        )}

        {/* Error Alert */}
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default RedshiftConnectPanel;
